import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, ChevronLeft, ChevronRight, Users as UsersIcon, ShieldCheck } from "lucide-react";
import { ADMIN } from "@/constants/testIds";

const PAGE_SIZE = 20;

export default function AdminUsers() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", { params: { q: q || undefined, page, page_size: PAGE_SIZE } });
      setRows(data.items);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }, [q, page]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <div className="flex items-baseline gap-2">
        <h1 className="font-display text-3xl font-extrabold flex items-center gap-2"><UsersIcon className="w-6 h-6 text-hyd-gold" /> Users</h1>
        <span className="text-hyd-text2 text-sm">{total} total</span>
      </div>

      <div className="mt-4 relative max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-hyd-gold" />
        <Input
          data-testid={ADMIN.userSearch}
          value={q}
          onChange={(e) => { setPage(1); setQ(e.target.value); }}
          placeholder="Search by phone or name…"
          className="pl-9 bg-hyd-panel border-hyd-border rounded-2xl h-10"
        />
      </div>

      <div className="mt-4 bg-hyd-panel border border-hyd-border rounded-3xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-hyd-text2">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-hyd-text3">No users found.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-hyd-panel2 text-hyd-text2 text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left p-3">Name / Phone</th>
                <th className="text-left p-3">Role</th>
                <th className="text-right p-3">Orders</th>
                <th className="text-right p-3">Total spent</th>
                <th className="text-left p-3">Joined</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hyd-border">
              {rows.map((u) => (
                <tr key={u.id} data-testid={ADMIN.userRow(u.id)} className="hover:bg-hyd-panel2/60">
                  <td className="p-3">
                    <div className="font-semibold">{u.name}</div>
                    <div className="text-xs text-hyd-text3">{u.phone}</div>
                  </td>
                  <td className="p-3">
                    {u.role === "admin"
                      ? <span className="inline-flex items-center gap-1 text-hyd-gold text-xs font-bold uppercase"><ShieldCheck className="w-3.5 h-3.5" /> Admin</span>
                      : <span className="text-hyd-text2 text-xs uppercase">Customer</span>}
                  </td>
                  <td className="p-3 text-right">{u.orders_count}</td>
                  <td className="p-3 text-right text-hyd-gold font-bold">{inr(u.total_spent)}</td>
                  <td className="p-3 text-xs text-hyd-text3">{new Date(u.created_at).toLocaleDateString()}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <Link to={`/admin/users/${u.id}`}>
                      <Button size="sm" variant="ghost" className="text-hyd-gold hover:bg-hyd-panel">View</Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="text-xs text-hyd-text2">
          Showing {rows.length ? (page - 1) * PAGE_SIZE + 1 : 0}–{(page - 1) * PAGE_SIZE + rows.length} of {total}
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} variant="secondary" className="bg-hyd-panel border border-hyd-border text-hyd-text2 rounded-full">
            <ChevronLeft className="w-4 h-4" /> Prev
          </Button>
          <div className="text-xs text-hyd-text2 min-w-[80px] text-center">Page {page} of {totalPages}</div>
          <Button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} variant="secondary" className="bg-hyd-panel border border-hyd-border text-hyd-text2 rounded-full">
            Next <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
